"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { thumb } from "@/lib/youtube";
import { HERO_VIDEOS } from "@/lib/content";

const ROTATE_MS = 30000;

export default function HomeHero({ videos = HERO_VIDEOS }) {
  const [resolvedVideos, setResolvedVideos] = useState(videos);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const timerRef = useRef(null);
  const sectionRef = useRef(null);
  const mediaRef = useRef(null);
  const contentRef = useRef(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const current = resolvedVideos[idx];
  const next1 = resolvedVideos[(idx + 1) % resolvedVideos.length];
  const next2 = resolvedVideos[(idx + 2) % resolvedVideos.length];

  useEffect(() => {
    setResolvedVideos(videos);
  }, [videos]);

  // resolve hero titles from YouTube API; fall back to oEmbed for missing IDs
  useEffect(() => {
    let cancelled = false;
    const fetchOfficialTitles = async () => {
      let byId = new Map();

      try {
        const res = await fetch("/api/youtube");
        const data = await res.json();
        const all = Array.isArray(data?.all) ? data.all : [];
        byId = new Map(all.map((v) => [v.id, v]));
      } catch {
        // Keep going; oEmbed fallback will still attempt title resolution.
      }

      const currentVideos = videos;
      const unresolved = currentVideos.filter((v) => !byId.get(v.id)?.title);
      if (unresolved.length) {
        const oembedResults = await Promise.allSettled(
          unresolved.map(async (v) => {
            const url = `https://www.youtube.com/watch?v=${v.id}`;
            const res = await fetch(
              `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`
            );
            if (!res.ok) return null;
            const data = await res.json();
            if (!data?.title) return null;
            return { id: v.id, title: data.title };
          })
        );

        for (const result of oembedResults) {
          if (result.status !== "fulfilled" || !result.value) continue;
          const existing = byId.get(result.value.id) || {};
          byId.set(result.value.id, { ...existing, title: result.value.title });
        }
      }

      if (cancelled) return;

      setResolvedVideos((prev) =>
        prev.map((v) => {
          const official = byId.get(v.id);
          if (!official?.title) return v;
          return {
            ...v,
            title: official.title,
            thumbnailHigh: official.thumbnailHigh || v.thumbnailHigh,
            thumbnail: official.thumbnail || v.thumbnail,
          };
        })
      );
    };

    fetchOfficialTitles();

    return () => {
      cancelled = true;
    };
  }, [videos]);

  // auto-advance
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % resolvedVideos.length);
    }, ROTATE_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, resolvedVideos.length]);

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const go = (d) => setIdx((i) => (i + d + resolvedVideos.length) % resolvedVideos.length);

  // iframe params — autoplay muted (needed for autoplay on mobile).
  // `origin` is passed so YouTube's widgetapi knows the exact parent
  // URL to postMessage back to (kills the "target origin" warnings
  // that spammed the console). Computed client-side only — empty on
  // SSR, filled in useEffect — so we don't hydrate-mismatch.
  const controlsParam = isMobileViewport ? 1 : 0;
  const [origin, setOrigin] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);
  const originParam = origin ? `&origin=${encodeURIComponent(origin)}` : "";
  const iframeSrc = `https://www.youtube.com/embed/${current.id}?autoplay=1&mute=1&controls=${controlsParam}&showinfo=0&modestbranding=1&loop=1&playlist=${current.id}&playsinline=1&enablejsapi=1&rel=0${originParam}`;

  const heroIframeRef = useRef(null);

  // Mute / unmute — sent on every toggle + re-sent on iframe load so
  // we don't lose state when YouTube swaps iframes on src change.
  useEffect(() => {
    const iframe = heroIframeRef.current;
    if (!iframe) return;

    const sendCommand = (func) => {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*"
      );
    };

    const syncState = () => {
      sendCommand(muted ? "mute" : "unMute");
      sendCommand("playVideo");
    };

    syncState();
    iframe.addEventListener("load", syncState);
    return () => iframe.removeEventListener("load", syncState);
  }, [muted, current.id]);

  // Autoplay recovery. Above-the-fold iframes on iOS routinely get
  // their initial autoplay attempt swallowed — the preloader is
  // covering the page at iframe-load time and Safari sees the page
  // as "not interacted yet". We delay the first nudge by 800ms so
  // YouTube's internal message handler has time to initialize (sending
  // postMessages before that throws `isExternalMethodAvailable`
  // inside YouTube's widgetapi), then retry every 700ms up to 5s.
  useEffect(() => {
    const iframe = heroIframeRef.current;
    if (!iframe) return;

    const tryPlay = () => {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }),
        "*"
      );
    };

    let interval;
    const startRetry = () => {
      // Clear any prior interval before starting a new one.
      if (interval) clearInterval(interval);
      tryPlay();
      interval = setInterval(tryPlay, 700);
      setTimeout(() => clearInterval(interval), 5000);
    };

    // Wait for the iframe to actually load, then give YouTube's
    // widgetapi ~800ms to wire up before we start spamming commands.
    const onLoad = () => setTimeout(startRetry, 800);
    iframe.addEventListener("load", onLoad);
    window.addEventListener("bo:preloader-done", tryPlay);

    // First user gesture anywhere = iOS's unlock moment.
    let fired = false;
    const kick = () => {
      if (fired) return;
      fired = true;
      tryPlay();
    };
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", kick, opts);
    window.addEventListener("click", kick, opts);
    window.addEventListener("scroll", kick, opts);

    return () => {
      iframe.removeEventListener("load", onLoad);
      if (interval) clearInterval(interval);
      window.removeEventListener("bo:preloader-done", tryPlay);
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("click", kick);
      window.removeEventListener("scroll", kick);
    };
  }, [current.id]);

  return (
    <section ref={sectionRef} className="relative h-[100svh] w-full overflow-hidden bg-black text-white">
      <div ref={mediaRef} className="absolute inset-0">
      {/* Poster thumbnail behind the iframe for instant paint */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={thumb(current.id, "maxresdefault")}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <iframe
        ref={heroIframeRef}
        key={current.id}
        src={iframeSrc}
        title={current.title}
        className={`absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.77vh] min-w-full -translate-x-1/2 -translate-y-1/2 ${
          isMobileViewport ? "pointer-events-auto" : "pointer-events-none"
        }`}
        allow="autoplay; encrypted-media; picture-in-picture"
      />
      </div>
      <div className="absolute inset-0 hero-gradient" />

      {/* Content */}
      <div
        ref={contentRef}
        className="relative z-10 flex h-full flex-col justify-between px-6 pb-10 pt-12 md:px-12 md:pb-16 md:pt-16"
      >
        <div />

        <div className="max-w-[1400px]">
          <div className="mt-6 flex flex-wrap items-center gap-4 md:mt-8">
            <span className="mono-label opacity-80">
              Neurosurgeon · Catan Champion · Renaissance Man
            </span>
          </div>

          <div className="mt-5 flex flex-col text-xs uppercase tracking-[0.18em] opacity-75 md:mt-6">
            <span className="opacity-60">Now playing</span>
            <span className="mt-1 max-w-[680px] text-[22px] normal-case tracking-normal opacity-100 line-clamp-2">
              {current.title}
            </span>
          </div>

          {/* Controls row */}
          <div className="flex items-end justify-between gap-6 md:mt-10">
            <div className="flex items-center gap-3">
              <button
                onClick={() => go(-1)}
                aria-label="Previous video"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur transition hover:bg-white/10"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => go(1)}
                aria-label="Next video"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur transition hover:bg-white/10"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "Unmute" : "Mute"}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur transition hover:bg-white/10"
              >
                {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
            </div>

            {/* Queue — next 2 thumbnails */}
            <div className="hidden items-center gap-3 md:flex">
              <span className="mono-label opacity-50">Up next</span>
              {[next1, next2].map((v, i) => (
                <button
                  key={v.id + i}
                  onClick={() => setIdx((idx + 1 + i) % resolvedVideos.length)}
                  className="group relative h-14 w-24 overflow-hidden rounded-md border border-white/15 transition hover:border-white/50"
                  aria-label={`Play ${v.title}`}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={thumb(v.id, "mqdefault")}
                    alt={v.title}
                    className="h-full w-full object-cover opacity-80 group-hover:opacity-100"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Dot nav */}
          <div className="mt-8 flex gap-2">
            {resolvedVideos.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Video ${i + 1}`}
                className={`h-1 rounded-full transition-all ${
                  i === idx ? "w-12 bg-white" : "w-6 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
